/**
 * AtomicMapPanel.tsx
 *
 * ANIMATED ORBITAL ATOMIC MAP VISUALIZATION
 *
 * Layout:
 * - CENTER: Period Name (Q1, Q2, etc.) - The Sun/Nucleus
 * - ORBIT 1: Department Initials (ENG, MKT, HR) - Planets rotating
 * - ORBIT 2: Individual Initials (JD, AS, MK) - Moons rotating opposite
 *
 * Features:
 * - Continuous orbital animation
 * - Connected nodes with lines
 * - Role-based filtering
 * - Color-coded by score
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Chip,
    Button,
    Slider,
    useTheme,
    useMediaQuery,
    keyframes
} from '@mui/material';
import {
    Business,
    Groups,
    Person,
    Close,
    Visibility,
    TrendingUp,
    ZoomIn,
    ZoomOut,
    CenterFocusStrong,
    PlayArrow,
    Pause,
    Edit,
    Check
} from '@mui/icons-material';
import { useAppTheme } from '@contexts/ThemeContext';

// =============================================================================
// TYPES
// =============================================================================

interface UserContext {
    id: string;
    role: string;
    dept_id: number | null;
    is_manager: boolean;
    name: string;
}

interface Permissions {
    canCreateOrgOKR: boolean;
    canEditOrgOKR: boolean;
    canLockOrgOKR: boolean;
    canViewAllTeams: boolean;
    canCreateTeamOKR: boolean;
    canEditTeamOKR: boolean;
    canViewDeptMembers: boolean;
    canCreateIndividualOKR: boolean;
    canEditMilestones: boolean;
    canCreateTeam: boolean;
    canManageUsers: boolean;
    canAddNotes: boolean;
}

interface AtomicNode {
    id: string;
    name: string;
    type: 'org' | 'team' | 'individual';
    score: number;
    deptId?: number;
    userId?: string;
    children?: AtomicNode[];
    initials?: string;
    angle?: number;
    orbitGroup?: number;
}

interface Department {
    id: number;
    dept_id?: number;
    dept_name?: string;
    name?: string;
}

interface Employee {
    id: string | number;
    name: string;
    dept_id?: number;
    department_id?: number;
}

interface AtomicMapPanelProps {
    data: any;
    onNodeClick: (node: AtomicNode | null) => void;
    selectedNode: AtomicNode | null;
    userContext: UserContext;
    permissions: Permissions;
    departments?: Department[];
    employees?: Employee[];
    periodName?: string;
    onEditScore?: (objectiveId: string, newScore: number, type: 'org' | 'team' | 'individual') => void;
}

// =============================================================================
// UTILITIES
// =============================================================================

const getScoreColor = (score: number): string => {
    if (score >= 100) return '#10b981'; // Emerald - Complete
    if (score >= 70) return '#22c55e';  // Green - Great progress
    if (score >= 50) return '#84cc16';  // Lime - Good progress
    if (score >= 30) return '#eab308';  // Amber - Needs attention
    if (score >= 1) return '#f97316';   // Orange - At risk
    return '#3b82f6';                    // Blue - Not started (looks better than gray)
};

const getScoreLabel = (score: number): string => {
    if (score >= 100) return 'Complete';
    if (score >= 50) return 'Good Progress';
    if (score >= 20) return 'Needs Attention';
    if (score >= 1) return 'At Risk';
    return 'Not Started';
};

const getInitials = (name: string, maxLen: number = 3): string => {
    if (!name || typeof name !== 'string') return '?';
    const words = name.trim().split(/\s+/).filter(w => w && w.length > 0);
    if (words.length === 0) return '?';
    if (words.length === 1) {
        return words[0].substring(0, maxLen).toUpperCase();
    }
    return words.map(w => w[0] || '').join('').substring(0, maxLen).toUpperCase() || '?';
};

// Vibrant color palette for nodes
const NODE_COLORS = [
    '#8b5cf6', // Violet
    '#06b6d4', // Cyan
    '#f59e0b', // Amber
    '#ec4899', // Pink
    '#14b8a6', // Teal
    '#f97316', // Orange
    '#6366f1', // Indigo
    '#84cc16', // Lime
    '#ef4444', // Red
    '#22c55e', // Green
];

const getNodeColor = (index: number): string => {
    return NODE_COLORS[index % NODE_COLORS.length];
};

// =============================================================================
// ANIMATIONS
// =============================================================================

const orbit1Animation = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const orbit2Animation = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(-360deg); }
`;

const counterRotate = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(-360deg); }
`;

const counterRotateReverse = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const pulseGlow = keyframes`
    0%, 100% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.4), 0 0 40px rgba(99, 102, 241, 0.2); }
    50% { box-shadow: 0 0 30px rgba(99, 102, 241, 0.6), 0 0 60px rgba(99, 102, 241, 0.3); }
`;

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const AtomicMapPanel: React.FC<AtomicMapPanelProps> = ({
    data,
    onNodeClick,
    selectedNode,
    userContext,
    permissions,
    departments = [],
    employees = [],
    periodName: propPeriodName,
    onEditScore
}) => {
    const theme = useTheme();
    const { isDarkMode } = useAppTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(450);
    const [zoom, setZoom] = useState(1);
    const [isAnimating, setIsAnimating] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editScore, setEditScore] = useState(0);

    // Measure container width for responsive canvas
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerWidth(entry.contentRect.width);
            }
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Canvas dimensions - responsive
    const canvasSize = Math.min(containerWidth - 16, 450);
    const scale = canvasSize / 450;
    const centerX = canvasSize / 2;
    const centerY = canvasSize / 2;

    // Orbit radii - scale with canvas
    const orbit1Radius = 100 * scale;
    const orbit2Radius = 170 * scale;

    // Node sizes - scale with canvas but keep minimum readable size
    const centerNodeSize = Math.max(80 * scale, 50);
    const orbit1NodeSize = Math.max(48 * scale, 32);
    const orbit2NodeSize = Math.max(36 * scale, 26);

    // Animation speeds (seconds for full rotation)
    const orbit1Speed = 30;
    const orbit2Speed = 45;

    // Parse data - use departments/employees props if provided
    const { periodName, orgScore, teams, individuals, orbit1Teams, orbit2Teams, orbit1Individuals, orbit2Individuals } = useMemo(() => {
        // Use period name from props if available
        let pName = 'Q1';
        if (propPeriodName) {
            const match = propPeriodName.match(/Q\d/i);
            pName = match ? match[0].toUpperCase() : propPeriodName.substring(0, 3);
        } else if (data?.period?.name) {
            const match = data.period.name.match(/Q\d/i);
            pName = match ? match[0].toUpperCase() : data.period.name.substring(0, 3);
        }

        // PRIORITY 1: Use atomic map data if available (has actual OKR data with scores)
        // But we'll supplement with departments/employees for complete view
        // Support both new format (orgObjectives array) and legacy (orgObjective single)
        const orgObjectivesArray = data?.orgObjectives || (data?.orgObjective ? [data.orgObjective] : []);
        const mergedOrg = orgObjectivesArray.length > 0 ? {
            teamObjectives: orgObjectivesArray.flatMap((o: any) => o.teamObjectives || []),
            score: orgObjectivesArray.reduce((sum: number, o: any) => sum + (o.score || 0), 0) / (orgObjectivesArray.length || 1),
            id: orgObjectivesArray[0]?.id,
            title: orgObjectivesArray.map((o: any) => o.title).join(', ')
        } : null;

        if (mergedOrg || (departments && departments.length > 0)) {
            const org = mergedOrg;

            const allTeamsData: AtomicNode[] = [];
            const individualsData: { node: AtomicNode; teamIndex: number }[] = [];
            const employeeIdsFromOKR = new Set<string>(); // Track employees from OKR data
            const deptIdsWithTeamObj = new Set<number>(); // Track departments that have team objectives
            const addedTeamNames = new Set<string>(); // Track team names to prevent duplicates

            // Define orbital groupings - teams that work closely together
            // Orbit 1: Marketing, Sales (customer-facing)
            // Orbit 2: Engineering, HR (internal operations)
            const orbit1DeptNames = ['marketing', 'sales'];
            const orbit2DeptNames = ['engineering', 'hr'];

            // First, add teams from OKR data (these have scores)
            if (org?.teamObjectives) {
                org.teamObjectives.forEach((team: any, tIdx: number) => {
                    // Managers and Admins see all teams in the map
                    // Only employees see just their own team
                    if (!permissions.canViewAllTeams && !permissions.canViewDeptMembers && Number(team.department_id) !== Number(userContext.dept_id)) {
                        return;
                    }

                    const teamName = team.dept_name || team.title || 'Team';
                    const teamNameKey = teamName.toLowerCase().trim();

                    // Skip if already added (prevent duplicates)
                    if (addedTeamNames.has(teamNameKey)) {
                        return;
                    }
                    addedTeamNames.add(teamNameKey);

                    deptIdsWithTeamObj.add(Number(team.department_id));
                    const deptName = teamNameKey;
                    const orbitGroup = orbit1DeptNames.some(n => deptName.includes(n)) ? 1 : 2;

                    allTeamsData.push({
                        id: team.id,
                        name: teamName,
                        type: 'team',
                        score: team.score || 0,
                        deptId: team.department_id,
                        initials: getInitials(teamName, 3),
                        orbitGroup: orbitGroup
                    });

                    const currentTeamIndex = allTeamsData.length - 1;

                    // Add individuals from OKR data
                    if (team.individualObjectives && team.individualObjectives.length > 0) {
                        team.individualObjectives.forEach((ind: any) => {
                            // Filter individuals by role
                            if (!permissions.canViewAllTeams && !permissions.canViewDeptMembers && ind.employee_id !== userContext.id) {
                                return;
                            }

                            employeeIdsFromOKR.add(String(ind.employee_id));
                            individualsData.push({
                                node: {
                                    id: ind.id,
                                    name: ind.employee_name || 'Employee',
                                    type: 'individual',
                                    score: ind.score || 0,
                                    userId: ind.employee_id,
                                    deptId: team.department_id,
                                    initials: getInitials(ind.employee_name || 'E', 2),
                                    orbitGroup: orbitGroup
                                },
                                teamIndex: currentTeamIndex
                            });
                        });
                    }
                });
            }

            // Add remaining departments that don't have team objectives yet
            // Admin/Manager: see all departments; Employee: see their own department
            if (departments && departments.length > 0) {
                departments.forEach((dept: any) => {
                    const deptId = Number(dept.id || dept.dept_id);
                    const deptName = dept.dept_name || dept.name || 'Dept';
                    const deptNameKey = deptName.toLowerCase().trim();

                    // Skip if already added (by dept_id OR by name)
                    if (deptIdsWithTeamObj.has(deptId) || addedTeamNames.has(deptNameKey)) {
                        return;
                    }

                    // For employees: only add their own department
                    if (!permissions.canViewAllTeams && !permissions.canViewDeptMembers) {
                        if (deptId !== Number(userContext.dept_id)) {
                            return;
                        }
                    }

                    addedTeamNames.add(deptNameKey);

                    const orbitGroup = orbit1DeptNames.some(n => deptNameKey.includes(n)) ? 1 : 2;

                    allTeamsData.push({
                        id: String(deptId),
                        name: deptName,
                        type: 'team',
                        score: 0,
                        deptId: deptId,
                        initials: getInitials(deptName, 3),
                        orbitGroup: orbitGroup
                    });
                });
            }

            // Now add employees to teams that don't have individual objectives
            if (employees && employees.length > 0) {
                // Filter employees based on permissions
                let filteredEmps = employees.filter(e => e.dept_id || e.department_id);

                if (!permissions.canViewAllTeams) {
                    if (permissions.canViewDeptMembers) {
                        // Manager: Show only employees in their department
                        filteredEmps = filteredEmps.filter(e => Number(e.dept_id || e.department_id) === Number(userContext.dept_id));
                    } else {
                        // Employee: Show only themselves
                        filteredEmps = filteredEmps.filter(e => String(e.id) === String(userContext.id));
                    }
                }

                // Add employees that aren't already in the OKR data
                filteredEmps.forEach(emp => {
                    if (!employeeIdsFromOKR.has(String(emp.id))) {
                        const empDeptId = Number(emp.dept_id || emp.department_id);
                        const teamIndex = allTeamsData.findIndex(t => Number(t.deptId) === empDeptId);

                        if (teamIndex >= 0) {
                            const team = allTeamsData[teamIndex];
                            individualsData.push({
                                node: {
                                    id: String(emp.id),
                                    name: emp.name || 'Employee',
                                    type: 'individual',
                                    score: 0,
                                    userId: String(emp.id),
                                    deptId: empDeptId,
                                    initials: getInitials(emp.name || 'E', 2),
                                    orbitGroup: team.orbitGroup
                                },
                                teamIndex: teamIndex
                            });
                        }
                    }
                });
            }

            // Separate teams into orbit groups
            const orbit1Teams = allTeamsData.filter(t => t.orbitGroup === 1);
            const orbit2Teams = allTeamsData.filter(t => t.orbitGroup === 2);

            // Separate individuals into orbit groups based on their team's orbit
            const orbit1Individuals = individualsData.filter(i => i.node.orbitGroup === 1);
            const orbit2Individuals = individualsData.filter(i => i.node.orbitGroup === 2);

            return {
                periodName: pName,
                orgScore: org?.score || 0,
                teams: allTeamsData,
                individuals: individualsData,
                orbit1Teams,
                orbit2Teams,
                orbit1Individuals,
                orbit2Individuals
            };
        }

        // Empty state - no departments or data available
        return { periodName: pName, orgScore: 0, teams: [], individuals: [], orbit1Teams: [], orbit2Teams: [], orbit1Individuals: [], orbit2Individuals: [] };
    }, [data, permissions, userContext, departments, employees, propPeriodName]);

    // Calculate positions for nodes
    const getOrbitPosition = (index: number, total: number, radius: number, offsetAngle: number = 0) => {
        const angle = (2 * Math.PI * index) / Math.max(total, 1) + offsetAngle - Math.PI / 2;
        return {
            x: radius * Math.cos(angle),
            y: radius * Math.sin(angle),
            angle: (angle * 180) / Math.PI
        };
    };

    const hasData = teams.length > 0 || individuals.length > 0 || (data?.orgObjectives && data.orgObjectives.length > 0) || data?.orgObjective || (departments && departments.length > 0);

    return (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: isDarkMode ? 'transparent' : 'transparent'
        }}>
            {/* Controls */}
            <Box sx={{
                display: 'flex',
                gap: 0.5,
                p: 1.5,
                justifyContent: 'center',
                alignItems: 'center',
                borderBottom: '1px solid',
                borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'
            }}>
                <Tooltip title={isAnimating ? "Pause" : "Play"}>
                    <IconButton
                        size="small"
                        onClick={() => setIsAnimating(!isAnimating)}
                        sx={{
                            bgcolor: isAnimating ? 'rgba(99, 102, 241, 0.15)' : isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                            color: isAnimating ? '#6366f1' : 'inherit',
                            '&:hover': { bgcolor: isAnimating ? 'rgba(99, 102, 241, 0.25)' : isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' }
                        }}
                    >
                        {isAnimating ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
                    </IconButton>
                </Tooltip>
                <Tooltip title="Zoom In">
                    <IconButton
                        size="small"
                        onClick={() => setZoom(z => Math.min(z + 0.15, 1.5))}
                        sx={{
                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                            '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' }
                        }}
                    >
                        <ZoomIn fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Zoom Out">
                    <IconButton
                        size="small"
                        onClick={() => setZoom(z => Math.max(z - 0.15, 0.5))}
                        sx={{
                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                            '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' }
                        }}
                    >
                        <ZoomOut fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Reset">
                    <IconButton
                        size="small"
                        onClick={() => setZoom(1)}
                        sx={{
                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                            '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' }
                        }}
                    >
                        <CenterFocusStrong fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Orbital Map Canvas */}
            <Box ref={containerRef} sx={{
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 0.5, sm: 1, md: 2 }
            }}>
                {hasData ? (
                    <Box sx={{
                        position: 'relative',
                        width: canvasSize,
                        height: canvasSize,
                        transform: `scale(${zoom})`,
                        transformOrigin: 'center center',
                        transition: 'transform 0.3s ease'
                    }}>
                        {/* Orbit Rings - Black Lines */}
                        <Box sx={{
                            position: 'absolute',
                            left: centerX - orbit1Radius,
                            top: centerY - orbit1Radius,
                            width: orbit1Radius * 2,
                            height: orbit1Radius * 2,
                            borderRadius: '50%',
                            border: `2px dashed ${isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.25)'}`,
                            pointerEvents: 'none'
                        }} />
                        <Box sx={{
                            position: 'absolute',
                            left: centerX - orbit2Radius,
                            top: centerY - orbit2Radius,
                            width: orbit2Radius * 2,
                            height: orbit2Radius * 2,
                            borderRadius: '50%',
                            border: `2px dashed ${isDarkMode ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.2)'}`,
                            pointerEvents: 'none'
                        }} />

                        {/* CENTER - Period/Org Goal (Sun/Nucleus) */}
                        <Tooltip title={`${(data?.orgObjectives?.[0]?.title || data?.orgObjective?.title || periodName || 'Organization Goal')}${orgScore > 0 ? ` - ${Math.round(orgScore)}%` : ''}`} arrow>
                            <Box
                                onClick={() => {
                                    const firstOrg = data?.orgObjectives?.[0] || data?.orgObjective;
                                    onNodeClick(firstOrg ? {
                                        id: firstOrg.id,
                                        name: firstOrg.title,
                                        type: 'org',
                                        score: orgScore
                                    } : { id: 'period', type: 'org', name: periodName, score: 0 } as AtomicNode);
                                }}
                                sx={{
                                    position: 'absolute',
                                    left: centerX - centerNodeSize / 2,
                                    top: centerY - centerNodeSize / 2,
                                    width: centerNodeSize,
                                    height: centerNodeSize,
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    border: `3px solid ${isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.9)'}`,
                                    boxShadow: '0 0 30px rgba(124, 58, 237, 0.4), 0 0 60px rgba(124, 58, 237, 0.2)',
                                    animation: `${pulseGlow} 3s ease-in-out infinite`,
                                    zIndex: 50,
                                    transition: 'transform 0.2s ease',
                                    '&:hover': {
                                        transform: 'scale(1.1)'
                                    }
                                }}
                            >
                                <Typography sx={{
                                    color: '#fff',
                                    fontWeight: 900,
                                    fontSize: `${Math.max(1.4 * scale, 0.85)}rem`,
                                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                    lineHeight: 1
                                }}>
                                    {periodName}
                                </Typography>
                                {orgScore > 0 && (
                                    <Typography sx={{
                                        color: 'rgba(255,255,255,0.9)',
                                        fontWeight: 700,
                                        fontSize: '0.7rem',
                                        mt: 0.25
                                    }}>
                                        {Math.round(orgScore)}%
                                    </Typography>
                                )}
                            </Box>
                        </Tooltip>

                        {/* ORBIT 1 - Marketing & Sales Teams (Rotating Container) */}
                        {orbit1Teams.length > 0 && (
                            <Box sx={{
                                position: 'absolute',
                                left: centerX - orbit1Radius,
                                top: centerY - orbit1Radius,
                                width: orbit1Radius * 2,
                                height: orbit1Radius * 2,
                                animation: isAnimating ? `${orbit1Animation} ${orbit1Speed}s linear infinite` : 'none',
                                pointerEvents: 'none'
                            }}>
                                {/* Teams on orbit 1 */}
                                {orbit1Teams.map((team, idx) => {
                                    const pos = getOrbitPosition(idx, orbit1Teams.length, orbit1Radius);
                                    const color = '#0ea5e9'; // Professional cyan for teams
                                    const isMyTeam = !permissions.canViewAllTeams ? Number(team.deptId) === Number(userContext.dept_id) : true;
                                    return (
                                        <Tooltip key={team.id} title={team.name} arrow>
                                            <Box
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (isMyTeam) onNodeClick(team);
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    left: orbit1Radius + pos.x - orbit1NodeSize / 2,
                                                    top: orbit1Radius + pos.y - orbit1NodeSize / 2,
                                                    width: orbit1NodeSize,
                                                    height: orbit1NodeSize,
                                                    borderRadius: '50%',
                                                    bgcolor: isMyTeam ? color : '#64748b',
                                                    opacity: isMyTeam ? 1 : 0.5,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: isMyTeam ? 'pointer' : 'default',
                                                    border: `2px solid ${isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.8)'}`,
                                                    boxShadow: isMyTeam ? `0 0 15px ${color}50, 0 4px 12px rgba(0,0,0,0.3)` : 'none',
                                                    pointerEvents: 'auto',
                                                    animation: isAnimating ? `${counterRotate} ${orbit1Speed}s linear infinite` : 'none',
                                                    transition: 'box-shadow 0.2s ease',
                                                    '&:hover': isMyTeam ? {
                                                        boxShadow: `0 0 25px ${color}70, 0 6px 20px rgba(0,0,0,0.4)`,
                                                        zIndex: 100
                                                    } : {}
                                                }}
                                            >
                                                <Typography sx={{
                                                    color: '#fff',
                                                    fontWeight: 800,
                                                    fontSize: '0.75rem',
                                                    textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                                    lineHeight: 1
                                                }}>
                                                    {team.initials}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        )}

                        {/* ORBIT 2 - Engineering & HR Teams (Rotating Container - Opposite Direction) */}
                        {orbit2Teams.length > 0 && (
                            <Box sx={{
                                position: 'absolute',
                                left: centerX - orbit2Radius,
                                top: centerY - orbit2Radius,
                                width: orbit2Radius * 2,
                                height: orbit2Radius * 2,
                                animation: isAnimating ? `${orbit2Animation} ${orbit2Speed}s linear infinite` : 'none',
                                pointerEvents: 'none'
                            }}>
                                {orbit2Teams.map((team, idx) => {
                                    const pos = getOrbitPosition(idx, orbit2Teams.length, orbit2Radius);
                                    const color = '#8b5cf6'; // Purple for orbit 2 teams
                                    const isMyTeam = !permissions.canViewAllTeams ? Number(team.deptId) === Number(userContext.dept_id) : true;
                                    return (
                                        <Tooltip key={team.id} title={team.name} arrow>
                                            <Box
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (isMyTeam) onNodeClick(team);
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    left: orbit2Radius + pos.x - orbit2NodeSize / 2,
                                                    top: orbit2Radius + pos.y - orbit2NodeSize / 2,
                                                    width: orbit2NodeSize,
                                                    height: orbit2NodeSize,
                                                    borderRadius: '50%',
                                                    bgcolor: isMyTeam ? color : '#64748b',
                                                    opacity: isMyTeam ? 1 : 0.5,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: isMyTeam ? 'pointer' : 'default',
                                                    border: `2px solid ${isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.8)'}`,
                                                    boxShadow: isMyTeam ? `0 0 10px ${color}40, 0 3px 8px rgba(0,0,0,0.25)` : 'none',
                                                    pointerEvents: 'auto',
                                                    animation: isAnimating ? `${counterRotateReverse} ${orbit2Speed}s linear infinite` : 'none',
                                                    transition: 'box-shadow 0.2s ease',
                                                    '&:hover': isMyTeam ? {
                                                        boxShadow: `0 0 20px ${color}60, 0 5px 15px rgba(0,0,0,0.35)`,
                                                        zIndex: 100
                                                    } : {}
                                                }}
                                            >
                                                <Typography sx={{
                                                    color: '#fff',
                                                    fontWeight: 800,
                                                    fontSize: '0.7rem',
                                                    textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                                    lineHeight: 1
                                                }}>
                                                    {team.initials}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        )}

                        {/* Legend */}
                        <Box sx={{
                            position: 'absolute',
                            bottom: 5,
                            left: 5,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.3,
                            p: 1,
                            bgcolor: isDarkMode ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.9)',
                            borderRadius: 2,
                            backdropFilter: 'blur(8px)',
                            border: '1px solid',
                            borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#7c3aed' }} />
                                <Typography sx={{ fontSize: '0.6rem', color: isDarkMode ? '#fff' : '#333' }}>Org Goal</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#0ea5e9' }} />
                                <Typography sx={{ fontSize: '0.6rem', color: isDarkMode ? '#fff' : '#333' }}>Marketing/Sales</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#8b5cf6' }} />
                                <Typography sx={{ fontSize: '0.6rem', color: isDarkMode ? '#fff' : '#333' }}>Engineering/HR</Typography>
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{
                        textAlign: 'center',
                        py: 6,
                        px: 3
                    }}>
                        <Box sx={{
                            width: 100,
                            height: 100,
                            borderRadius: '50%',
                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2
                        }}>
                            <Business sx={{
                                fontSize: 48,
                                color: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'
                            }} />
                        </Box>
                        <Typography sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            mb: 0.5
                        }}>
                            No OKR Data Yet
                        </Typography>
                        <Typography sx={{
                            color: 'text.disabled',
                            fontSize: '0.75rem'
                        }}>
                            Create objectives to see the orbital map
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Selected Node Details */}
            {selectedNode && (
                <Box sx={{
                    borderTop: '1px solid',
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    p: 2,
                    bgcolor: isDarkMode ? 'rgba(0,0,0,0.3)' : '#f8fafc'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{
                                fontWeight: 800,
                                fontSize: '0.95rem',
                                letterSpacing: '-0.01em',
                                mb: 0.5
                            }}>
                                {selectedNode.name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Chip
                                    size="small"
                                    label={(selectedNode.type || 'node').toUpperCase()}
                                    sx={{
                                        fontSize: '0.6rem',
                                        fontWeight: 700,
                                        height: 20,
                                        bgcolor: selectedNode.type === 'org' ? 'rgba(99, 102, 241, 0.15)'
                                            : selectedNode.type === 'team' ? 'rgba(6, 182, 212, 0.15)'
                                                : 'rgba(16, 185, 129, 0.15)',
                                        color: selectedNode.type === 'org' ? '#6366f1'
                                            : selectedNode.type === 'team' ? '#06b6d4'
                                                : '#10b981'
                                    }}
                                />
                                <Chip
                                    size="small"
                                    label={`${Math.round(isEditing ? editScore : selectedNode.score)}%`}
                                    sx={{
                                        fontSize: '0.65rem',
                                        fontWeight: 800,
                                        height: 20,
                                        bgcolor: getScoreColor(isEditing ? editScore : selectedNode.score),
                                        color: '#fff'
                                    }}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {/* Edit Score Button - Only for Org objectives and if permission exists */}
                            {selectedNode.type === 'org' && onEditScore && permissions.canEditOrgOKR && (
                                <Tooltip title={isEditing ? "Save" : "Edit Score"}>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            if (isEditing) {
                                                // Save
                                                onEditScore(selectedNode.id, editScore, selectedNode.type);
                                                setIsEditing(false);
                                            } else {
                                                // Start editing
                                                setEditScore(selectedNode.score);
                                                setIsEditing(true);
                                            }
                                        }}
                                        sx={{
                                            bgcolor: isEditing
                                                ? 'rgba(16, 185, 129, 0.15)'
                                                : isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                            color: isEditing ? '#10b981' : 'inherit',
                                            width: 28,
                                            height: 28,
                                            '&:hover': {
                                                bgcolor: isEditing
                                                    ? 'rgba(16, 185, 129, 0.25)'
                                                    : isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'
                                            }
                                        }}
                                    >
                                        {isEditing ? <Check sx={{ fontSize: 16 }} /> : <Edit sx={{ fontSize: 16 }} />}
                                    </IconButton>
                                </Tooltip>
                            )}
                            <IconButton
                                size="small"
                                onClick={() => {
                                    setIsEditing(false);
                                    onNodeClick(null);
                                }}
                                sx={{
                                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                    width: 28,
                                    height: 28
                                }}
                            >
                                <Close sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Edit Score Slider */}
                    {isEditing && (
                        <Box sx={{ mb: 1.5, px: 0.5 }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', mb: 1 }}>
                                Adjust Progress: {Math.round(editScore)}%
                            </Typography>
                            <Slider
                                value={editScore}
                                onChange={(_, value) => setEditScore(value as number)}
                                min={0}
                                max={100}
                                step={1}
                                sx={{
                                    height: 6,
                                    '& .MuiSlider-thumb': {
                                        width: 16,
                                        height: 16,
                                        bgcolor: getScoreColor(editScore),
                                        '&:hover, &.Mui-focusVisible': {
                                            boxShadow: `0 0 10px ${getScoreColor(editScore)}80`
                                        }
                                    },
                                    '& .MuiSlider-track': {
                                        bgcolor: getScoreColor(editScore),
                                        border: 'none'
                                    },
                                    '& .MuiSlider-rail': {
                                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.15)' : '#e2e8f0'
                                    }
                                }}
                            />
                        </Box>
                    )}

                    {/* Progress Bar */}
                    {!isEditing && (
                        <Box sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
                            overflow: 'hidden',
                            mb: 1
                        }}>
                            <Box sx={{
                                height: '100%',
                                width: `${Math.min(selectedNode.score, 100)}%`,
                                borderRadius: 3,
                                bgcolor: getScoreColor(selectedNode.score),
                                transition: 'width 0.4s ease',
                                boxShadow: `0 0 8px ${getScoreColor(selectedNode.score)}50`
                            }} />
                        </Box>
                    )}
                    <Typography sx={{
                        fontSize: '0.65rem',
                        color: 'text.secondary',
                        fontWeight: 600
                    }}>
                        Status: <Box component="span" sx={{ color: getScoreColor(isEditing ? editScore : selectedNode.score) }}>{getScoreLabel(isEditing ? editScore : selectedNode.score)}</Box>
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default AtomicMapPanel;
